import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  IconBuildingBank,
  IconDashboard,
  IconFileText,
  IconHeadphones,
  IconInfoCircle,
  IconLock,
  IconLogout,
  IconPlus,
  IconSearch,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../ui/Button";
import Text from "../ui/Text";

const UserMenu = ({ open, onClose }) => {
  const { logout, currentUser } = useAuth();
  const isLoggedIn = !!currentUser;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      // Handle logout error if needed
    }
    onClose();
  };

  // Menu items for logged in users
  const accountMenuItems = [
    {
      text: "Dashboard",
      icon: <IconDashboard size={20} />,
      path: "/dashboard",
    },
    {
      text: "Search games",
      icon: <IconSearch size={20} />,
      path: "/upcoming",
    },
    {
      text: "Create game",
      icon: <IconPlus size={20} />,
      path: "/create-game",
    },
    {
      text: "Payments",
      icon: <IconBuildingBank size={20} />,
      path: "/payments",
    },
    {
      text: "Settings",
      icon: <IconSettings size={20} />,
      path: "/settings",
    },
  ];

  const documentationMenuItems = [
    {
      text: "Terms and conditions",
      icon: <IconFileText size={20} />,
      path: "https://slow-centipede-6ab.notion.site/Terms-and-Conditions-2843273d4063801d9ae2f13950d02c34",
    },
    {
      text: "Privacy policy",
      icon: <IconLock size={20} />,
      path: "https://slow-centipede-6ab.notion.site/Privacy-Policy-2843273d40638050b960fcf0dbd1f122",
    },
    { text: "About", icon: <IconInfoCircle size={20} />, path: "/about" },
    {
      text: "Support",
      icon: <IconHeadphones size={20} />,
      path: "mailto:liam@findaside.app",
    },
  ];

  // Menu items for logged out users
  const guestMenuItems = [
    {
      text: "Search games",
      icon: <IconSearch size={20} />,
      path: "/upcoming",
    },
    { text: "About", icon: <IconInfoCircle size={20} />, path: "/about" },
  ];

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            top: "0px",
          },
        },
      }}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "100%" : 350,
            height: "100%",
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          borderBottom: "1px solid #E8E8E8",
        }}
      >
        <IconButton onClick={onClose} size="small">
          <IconX size={20} />
        </IconButton>
      </Box>

      {/* currentUser Profile Section (Only when logged in) */}
      {isLoggedIn && (
        <Box
          component={Link}
          to="/my-profile"
          onClick={onClose}
          sx={{
            textDecoration: "none",
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            p: 2,
            gap: 2,
            mb: 1,
            cursor: "pointer",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#F6F6F6",
            },
          }}
        >
          <Avatar
            src={currentUser?.profile_image_url}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: "#1890FF",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {currentUser.first_name[0].toUpperCase()}
            {currentUser?.last_name?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ display: "grid" }}>
            <Text
              variant="subtitle2"
              sx={{
                fontSize: "16px",
                mb: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentUser.first_name} {currentUser.last_name}
            </Text>
            <Text variant="paragraph3" sx={{ color: "#666" }}>
              Game credit: €{currentUser.gameCredit || "0.00"}
            </Text>
          </Box>
          <Box>
            <Text sx={{ color: "#1D81EF", whiteSpace: "nowrap" }}>
              View my profile
            </Text>
          </Box>
        </Box>
      )}

      {/* Menu Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {isLoggedIn ? (
          <>
            {/* Account Section */}
            <Box sx={{ px: 2 }}>
              <Text variant="subtitle1" sx={{ mb: "12px" }}>
                Account
              </Text>
              <List sx={{ p: 0 }}>
                {accountMenuItems.map((item) => (
                  <ListItem
                    key={item.text}
                    component={Link}
                    to={item.path}
                    target={item.path.startsWith("http") ? "_blank" : undefined}
                    rel={
                      item.path.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={onClose}
                    sx={{
                      py: 1.5,
                      px: 0,
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#F6F6F6",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "black",
                        minWidth: "0",
                        mr: "16px",
                        "& svg": { fontSize: "20px" },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "#000",
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Documentation Section */}
            <Box sx={{ p: 2 }}>
              <Text variant="subtitle1" sx={{ mb: "12px" }}>
                Documentation
              </Text>

              <List sx={{ p: 0 }}>
                {documentationMenuItems.map((item) => (
                  <ListItem
                    component={Link}
                    to={item.path}
                    target={item.path.startsWith("http") ? "_blank" : undefined}
                    rel={
                      item.path.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={onClose}
                    key={item.text}
                    sx={{
                      py: 1.5,
                      px: 0,
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#F6F6F6",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "black",
                        minWidth: "0",
                        mr: "16px",
                        "& svg": { fontSize: "20px" },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "#000",
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Logout */}
            <Box sx={{ p: 2 }}>
              <ListItem
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  px: 0,
                  cursor: "pointer",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#F6F6F6",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "black",
                    minWidth: "40px",
                    "& svg": { fontSize: "20px" },
                  }}
                >
                  <IconLogout size={20} />
                </ListItemIcon>
                <ListItemText
                  primary="Log out"
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#000",
                    },
                  }}
                />
              </ListItem>
            </Box>
          </>
        ) : (
          <>
            {/* Guest Menu Items */}
            <Box sx={{ p: 2 }}>
              <List sx={{ p: 0 }}>
                {guestMenuItems.map((item) => (
                  <ListItem
                    key={item.text}
                    component={Link}
                    to={item.path.startsWith("http") ? undefined : item.path}
                    target={item.path.startsWith("http") ? "_blank" : undefined}
                    rel={
                      item.path.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={onClose}
                    sx={{
                      py: 1.5,
                      px: 0,
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#F6F6F6",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "black",
                        minWidth: "40px",
                        "& svg": { fontSize: "20px" },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "#000",
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </Box>

      {/* Login Button (Only when logged out) */}
      {!isLoggedIn && (
        <Box sx={{ p: 2, borderTop: "1px solid #E8E8E8" }}>
          <Button
            component={Link}
            to="/signin"
            variant="primary"
            sx={{
              width: "100%",
              py: 1.5,
              fontSize: "16px",
            }}
          >
            Login
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default UserMenu;
